import { useEffect, useState } from "react";

import { BoardAppointmentItem } from "components/AppointmentsList/components/AppointmentsBoardView/BoardAppointmentItem/BoardAppointmentItem";
import { BoardAvailableSlot } from "components/AppointmentsList/components/AppointmentsBoardView/BoardAvailableSlot/BoardAvailableSlot";
import { RecurringExceptionItem } from "components/AppointmentsList/components/ConsultationItem/RecurringExceptionItem/RecurringExceptionItem";
import { StandardExceptionItem } from "components/AppointmentsList/components/ConsultationItem/StandardExceptionItem/StandardExceptionItem";
import { UnavailableItem } from "components/AppointmentsList/components/ConsultationItem/UnavailableItem/UnavailableItem";
import { SCHEDULE_ITEM_STATUS } from "components/AppointmentsList/constants/scheduleItemStatus";
import {
  assignOverlapLanes,
  isAppointmentCardItem,
  isExceptionItem,
  isUnavailableBlockItem,
} from "components/AppointmentsList/utils/assign-overlap-lanes";
import { getScheduleItems } from "components/AppointmentsList/utils/get-schedule-items";

import styles from "./AppointmentsItems.module.css";

// Breathing room between two cards sharing a slot.
const LANE_GAP_PX = 4;
// Each layer stacked on another steps in from the right by this much, so an
// unavailable stretch, the exception on it and the appointment on top all stay
// visible — and each keeps its own actions menu reachable (KIN-1591). Items
// with nothing underneath keep the full width.
const STACK_STEP_PX = 35;

const getItemKey = (doctor, item, index) =>
  `${doctor?.id || "unknown"}-${item.start?.getTime() || "no-date"}-${index}`;

const getStackGeometry = (item) => {
  const inset = (item.depth || 0) * STACK_STEP_PX;
  const lane = item.lane;

  if (!lane || lane.count <= 1) {
    return {
      "--lane-width": `calc(100% - ${inset}px)`,
      "--lane-offset": "0px",
    };
  }

  const reserved = inset + (lane.count - 1) * LANE_GAP_PX;
  const singleLane = `calc((100% - ${reserved}px) / ${lane.count})`;

  return {
    "--lane-width": singleLane,
    "--lane-offset": `calc(${singleLane} * ${lane.index} + ${
      lane.index * LANE_GAP_PX
    }px)`,
  };
};

export const AppointmentsItems = ({
  doctor,
  timeSlots,
  setSelectedAppointment,
  setSelectedDoctor,
  setShowAddAppointmentModal,
  setAppointmentType,
  onAppointmentClick,
  rowDate,
  selectedAppointments,
  onCheckboxToggle,
  slotDurationMinutes,
}) => {
  // Overlapping items hide one another, so clicking one brings it out in full:
  // a card widens over its neighbours, an exception fades out the cards on top
  // of it. Clicking it again — or another item — puts everything back.
  const [expandedItemKey, setExpandedItemKey] = useState(null);

  // Anything clicked outside the open item closes it again — another card, an
  // empty slot, a different doctor's column, the page around the board.
  useEffect(() => {
    if (!expandedItemKey) {
      return undefined;
    }

    const closeOnOutsideClick = (event) => {
      if (event.target.closest(`.${styles.expandedItem}`)) {
        return;
      }

      setExpandedItemKey(null);
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);

    return () =>
      document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, [expandedItemKey]);

  const scheduleItems = assignOverlapLanes(
    getScheduleItems(doctor, timeSlots, rowDate, slotDurationMinutes)
  );

  const expandedItem = scheduleItems.find(
    (item, index) => getItemKey(doctor, item, index) === expandedItemKey
  );
  const isExceptionExpanded = !!expandedItem && isExceptionItem(expandedItem);

  return (
    <>
      {scheduleItems.map((item, index) => {
        const isUnavailableAppointment =
          item.status === SCHEDULE_ITEM_STATUS.UNAVAILABLE && item.meeting;

        const isAppointmentCard = isAppointmentCardItem(item);
        const isUnavailableBlock = isUnavailableBlockItem(item);
        const isSharingRows = item.lane?.count > 1;
        const itemKey = getItemKey(doctor, item, index);
        const isExpanded = expandedItemKey === itemKey;
        // An expanded exception needs the cards covering it out of the way.
        const isFadedByException =
          isExceptionExpanded && item.lane?.overlapsException;

        const openConsultationEdit = () => {
          setAppointmentType("appointment");
          setSelectedAppointment(item);
          setSelectedDoctor(doctor);
          setShowAddAppointmentModal(true);
        };

        // Buttons, checkboxes and links keep their own behaviour — only clicks
        // on the item's own surface toggle it.
        const toggleExpanded = (event) => {
          if (event.target.closest("button, input, a")) {
            return;
          }

          setExpandedItemKey((current) =>
            current === itemKey ? null : itemKey
          );
        };

        const wrapperClassName = [
          isAppointmentCard ? styles.foregroundItem : styles.backgroundItem,
          isExceptionItem(item) ? styles.exceptionItem : "",
          isUnavailableBlock ? styles.unavailableBlockItem : "",
          isAppointmentCard || isExceptionItem(item) ? styles.laneItem : "",
          isExpanded ? styles.expandedItem : "",
          isFadedByException ? styles.fadedItem : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <div
            key={itemKey}
            className={wrapperClassName}
            onClick={toggleExpanded}
            style={{
              // Pin every item to its real time row AND to column 1. Pinning
              // the row keeps a card aligned to its time even when a longer
              // item overlaps it; pinning the column stops the grid
              // auto-placer from shoving an overlapping card into a new
              // implicit column (which bled into the next doctor's column).
              gridColumnStart: 1,
              gridRowStart:
                typeof item.slotIndex === "number"
                  ? item.slotIndex + 1
                  : undefined,
              gridRowEnd:
                item.status === SCHEDULE_ITEM_STATUS.CONSULTATION ||
                item.status === SCHEDULE_ITEM_STATUS.STANDARD_EXCEPTION ||
                item.status === SCHEDULE_ITEM_STATUS.RECURRING_EXCEPTION ||
                isUnavailableAppointment
                  ? `span ${item.rowSpan || 1}`
                  : undefined,
              ...(isAppointmentCard || isExceptionItem(item)
                ? getStackGeometry(item)
                : {}),
            }}
          >
            {item.status === SCHEDULE_ITEM_STATUS.UNAVAILABLE &&
              !item.meeting && <UnavailableItem />}
            {isUnavailableAppointment && (
              <BoardAppointmentItem
                item={item}
                doctor={doctor}
                onAppointmentClick={onAppointmentClick}
                onConsultationEdit={openConsultationEdit}
                isSelected={
                  selectedAppointments?.[item?.meeting?.consultation?.id]
                }
                onCheckboxToggle={onCheckboxToggle}
                slotDurationMinutes={slotDurationMinutes}
                compact={isSharingRows && !isExpanded}
              />
            )}
            {item.status === SCHEDULE_ITEM_STATUS.AVAILABLE && (
              <BoardAvailableSlot
                item={item}
                doctor={doctor}
                slotDurationMinutes={slotDurationMinutes}
                onAddConsultation={() => {
                  setSelectedAppointment(item);
                  setSelectedDoctor(doctor);
                  setShowAddAppointmentModal(true);
                  setAppointmentType(undefined);
                }}
              />
            )}
            {item.status === SCHEDULE_ITEM_STATUS.CONSULTATION && (
              <BoardAppointmentItem
                item={item}
                doctor={doctor}
                onAppointmentClick={onAppointmentClick}
                onConsultationEdit={openConsultationEdit}
                isSelected={
                  selectedAppointments?.[item?.meeting?.consultation?.id]
                }
                onCheckboxToggle={onCheckboxToggle}
                slotDurationMinutes={slotDurationMinutes}
                compact={isSharingRows && !isExpanded}
              />
            )}
            {item.status === SCHEDULE_ITEM_STATUS.STANDARD_EXCEPTION && (
              <StandardExceptionItem
                item={item}
                timeSlots={timeSlots}
                onEditException={() => {
                  setAppointmentType("standard-exception");
                  setSelectedAppointment(item);
                  setSelectedDoctor(doctor);
                  setShowAddAppointmentModal(true);
                }}
              />
            )}
            {item.status === SCHEDULE_ITEM_STATUS.RECURRING_EXCEPTION && (
              <RecurringExceptionItem
                item={item}
                timeSlots={timeSlots}
                onEditException={() => {
                  setAppointmentType("recurring-exception");
                  setSelectedAppointment(item);
                  setSelectedDoctor(doctor);
                  setShowAddAppointmentModal(true);
                }}
              />
            )}
          </div>
        );
      })}
    </>
  );
};
